import * as React from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { acquireAccessToken } from '../lib/api-client';
import {
    initMsal,
    isAuthenticationRedirectInProgress,
} from '../lib/msal';

interface AuthenticationBootstrapProps {
    createAuthenticatedContent: () => React.ReactNode;
}

export function SigningOn() {
    return (
        <div
            role="status"
            aria-live="polite"
            style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '100vh',
            }}
        >
            <Spinner size={SpinnerSize.large} label="Signing you on..." />
        </div>
    );
}

export function AuthenticationFailure() {
    return (
        <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif' }}>
            <h2>Authentication failed</h2>
            <p>Please refresh the page. If the problem persists, contact support.</p>
        </div>
    );
}

export function AuthenticationBootstrap({ createAuthenticatedContent }: AuthenticationBootstrapProps) {
    const contentFactory = React.useRef(createAuthenticatedContent);
    const [authenticatedContent, setAuthenticatedContent] = React.useState<React.ReactNode>(null);
    const [failed, setFailed] = React.useState(false);

    React.useEffect(() => {
        let isMounted = true;

        const authenticate = async () => {
            try {
                await initMsal();
                await acquireAccessToken();
                if (isMounted) {
                    setAuthenticatedContent(contentFactory.current());
                }
            } catch (error) {
                if (!isAuthenticationRedirectInProgress(error) && isMounted) {
                    console.error('Authentication startup failed', error);
                    setFailed(true);
                }
            }
        };

        void authenticate();
        return () => {
            isMounted = false;
        };
    }, []);

    if (failed) {
        return <AuthenticationFailure />;
    }

    return authenticatedContent ?? <SigningOn />;
}
